import { adminMutate } from '../../../admin/primitives.js'
import { messageEchecMutation } from '../../api.js'

/**
 * REGISTRE — UNE façon d'écrire dans Sonde (21/10/2026).
 *
 * Le navigateur passe TOUJOURS par le relais de Gestion
 * (`/api/v1/supervision/v/<chemin de Sonde sans /v1/>`), qui exige le droit,
 * le step-up et le mode sûr. Chaque écriture porte une Idempotency-Key neuve
 * (un double clic ne crée pas deux postes) et, pour une fiche, son ETag en
 * If-Match. `adminMutate` rejoue après step-up avec les MÊMES en-têtes.
 *
 * Résultat : `ok`, le corps de Sonde, et en cas d'échec le code (repris tel
 * quel de Sonde : DEVICE_IS_POWER_ROOT, COUNT_ONLY_VLAN, PREVIEW_OUTDATED,
 * PRECONDITION_FAILED…), le message en clair et les détails.
 */

export interface Ecriture {
  ok: boolean
  status: number
  data: unknown
  code: string | null
  message: string
  details: Record<string, unknown> | null
}

function cleIdempotence(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const o = new Uint8Array(16)
  c.getRandomValues(o)
  o[6] = (o[6]! & 0x0f) | 0x40
  o[8] = (o[8]! & 0x3f) | 0x80
  const h = [...o].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

export async function ecrireSonde(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', chemin: string, corps?: unknown,
  opts: { etag?: string | null; rien?: string } = {},
): Promise<Ecriture> {
  const headers: Record<string, string> = { 'Idempotency-Key': cleIdempotence() }
  if (opts.etag) headers['If-Match'] = opts.etag
  const rien = opts.rien ?? 'rien n’a été modifié'
  try {
    const r = await adminMutate<{ error?: string; message?: string; details?: Record<string, unknown> }>(
      method, `/api/v1/supervision/v/${chemin.replace(/^\/+/, '')}`, corps, { headers, deconnexionExplicite: true },
    )
    if (r.status >= 200 && r.status < 300) return { ok: true, status: r.status, data: r.data, code: null, message: '', details: null }
    const code = objet(r.data) && typeof r.data['error'] === 'string' ? r.data['error'] : null
    const details = objet(r.data) && objet(r.data['details']) ? r.data['details'] : null
    return { ok: false, status: r.status, data: r.data, code, message: messageEchecMutation(r.status, r.data, rien), details }
  } catch {
    return { ok: false, status: 0, data: null, code: null, message: `Gestion n’a pas répondu — ${rien}.`, details: null }
  }
}

/** Un ETag périmé (412 de Sonde, rendu 409 PRECONDITION_FAILED par le relais). */
export const estConflitVersion = (e: Ecriture): boolean =>
  e.code === 'PRECONDITION_FAILED' || e.code === 'IF_MATCH_REQUIRED' || e.status === 412
